/**
 * Variant family grouping for A/B/C sequence-step testing.
 *
 * A "family" is one original step plus the variants that test against it.
 * EmailBison returns variants with `is_variant: true`, `variant_from_step_id`
 * pointing at the original, and `order: null` (the original carries the slot's
 * order). This is the single source of family/ordering/labeling logic — every
 * read surface and the insights breakdown consume it; none re-implement it.
 */

/** Max messages per slot (original + variant). Bump to lift the A/B cap. */
export const MAX_MESSAGES_PER_STEP = 2

export interface VariantFamilyStep {
  id: number
  order?: number | null
  is_variant?: boolean
  variant_from_step_id?: number | null
}

export interface LabeledVariant<T> {
  step: T
  /** Stable display label, e.g. "Variant A". Shared across every surface. */
  label: string
}

export interface SequenceFamily<T> {
  original: T
  /**
   * The original's A/B/C label. "Variant A" once the family has at least one
   * variant, else null (an unsplit step shows no letter). Variants only ever
   * exist alongside their original, so when any variant is present the original
   * is the A of the set and the variants run B, C, ...
   */
  originalLabel: string | null
  variants: LabeledVariant<T>[]
}

const VARIANT_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/**
 * Stable display label for the Nth variant in a family (0-indexed).
 * Shared by id-based grouping (read surfaces) and the adjacency-based authoring
 * form, so a variant reads as the same "Variant A/B/C" everywhere.
 */
export function variantLabelForIndex(index: number): string {
  return index < VARIANT_LETTERS.length
    ? `Variant ${VARIANT_LETTERS[index]}`
    : `Variant ${index + 1}`
}

/**
 * Group a flat step list into variant families.
 *
 * When `enabled` is false (feature flag off), variants are dropped entirely so
 * only originals render — collapsing the UI to its pre-feature shape. A variant
 * whose `variant_from_step_id` matches no original is treated as its own
 * original so nothing is silently lost.
 */
export function groupStepsByFamily<T extends VariantFamilyStep>(
  steps: T[],
  { enabled }: { enabled: boolean }
): SequenceFamily<T>[] {
  const originals = steps.filter((step) => !step.is_variant)
  const originalIds = new Set(originals.map((step) => step.id))

  const variantsByParent = new Map<number, T[]>()
  const orphans: T[] = []

  if (enabled) {
    for (const step of steps) {
      if (!step.is_variant) continue
      const parentId = step.variant_from_step_id
      if (parentId != null && originalIds.has(parentId)) {
        const list = variantsByParent.get(parentId) ?? []
        list.push(step)
        variantsByParent.set(parentId, list)
      } else {
        orphans.push(step)
      }
    }
  }

  const families: SequenceFamily<T>[] = [...originals, ...orphans].map(
    (original) => {
      const variants = (variantsByParent.get(original.id) ?? [])
        .slice()
        .sort((a, b) => a.id - b.id)
        // The original is "Variant A", so variants start at B (index + 1).
        .map((step, index) => ({
          step,
          label: variantLabelForIndex(index + 1),
        }))
      return {
        original,
        originalLabel: variants.length > 0 ? variantLabelForIndex(0) : null,
        variants,
      }
    }
  )

  // Originals carry a real order; orphans/edge rows with null order sort last.
  families.sort((a, b) => {
    const aOrder = a.original.order ?? Number.MAX_SAFE_INTEGER
    const bOrder = b.original.order ?? Number.MAX_SAFE_INTEGER
    return aOrder !== bOrder ? aOrder - bOrder : a.original.id - b.original.id
  })

  return families
}

export interface FlatFamilyRow<T> {
  step: T
  /** 1-based slot number (the family's position). */
  familyNumber: number
  /** The variant's labeled entry, or null for the family original. */
  variant: LabeledVariant<T> | null
  /**
   * The row's A/B/C label, unified across originals and variants: the original's
   * `originalLabel` ("Variant A" in a split family, else null) for an original
   * row, or the variant's label ("Variant B"/"C"/...) for a variant row.
   * Consumers format `Step N · <label>` and branch on label presence rather than
   * on `variant != null`.
   */
  label: string | null
}

/**
 * Flatten grouped families into a display-ordered row list: each family's
 * original followed by its variants. Read surfaces share this so the
 * "originals then variants, numbered by slot" ordering lives in one place;
 * each caller formats its own label/badge from `familyNumber` and `variant`.
 *
 * @param families - Families from groupStepsByFamily.
 * @returns Flat rows in render order, each tagged with its slot number.
 */
export function flattenFamilies<T>(
  families: SequenceFamily<T>[]
): FlatFamilyRow<T>[] {
  const rows: FlatFamilyRow<T>[] = []
  families.forEach((family, index) => {
    rows.push({
      step: family.original,
      familyNumber: index + 1,
      variant: null,
      label: family.originalLabel,
    })
    family.variants.forEach((variant) =>
      rows.push({
        step: variant.step,
        familyNumber: index + 1,
        variant,
        label: variant.label,
      })
    )
  })
  return rows
}

export interface VariantWriteInput {
  id?: number
  is_variant?: boolean
  wait_in_days: number
}

export interface VariantWriteFields {
  order: number
  is_variant: boolean
  wait_in_days: number
  variant_from_step?: number
  variant_from_step_id?: number | null
}

/**
 * Resolve the EmailBison write fields for a flat, adjacency-ordered step list
 * (variants kept right after their family original). For each row it computes:
 *
 * - `order` — a unique, sequential slot order (the contract rejects duplicates,
 *   variants included).
 * - `wait_in_days` — for a variant, the family original's shared wait, so timing
 *   cannot diverge within a slot.
 * - exactly one link field for a variant: `variant_from_step_id` = the original's
 *   saved id when it has one, otherwise `variant_from_step` = the original's
 *   order in this same payload. Never both (the backend guard rejects that).
 *
 * A variant with no preceding original is treated as a standalone original — the
 * authoring UI never produces this, but the helper degrades safely.
 * See docs/solutions/emailbison-variant-write-contract.md.
 */
export function resolveVariantWriteFields<T extends VariantWriteInput>(
  steps: T[]
): VariantWriteFields[] {
  let lastOriginalIndex = -1
  return steps.map((step, index) => {
    const order = index + 1
    const isVariant = !!step.is_variant && lastOriginalIndex >= 0
    if (!isVariant) {
      if (!step.is_variant) lastOriginalIndex = index
      return {
        order,
        is_variant: false,
        wait_in_days: Math.max(1, step.wait_in_days),
      }
    }
    const original = steps[lastOriginalIndex]
    const fields: VariantWriteFields = {
      order,
      is_variant: true,
      wait_in_days: Math.max(1, original?.wait_in_days ?? step.wait_in_days),
    }
    if (original?.id != null) {
      fields.variant_from_step_id = original.id
    } else {
      fields.variant_from_step = lastOriginalIndex + 1
    }
    return fields
  })
}
